import React from 'react';
import styled from 'styled-components';
import { Trans, t } from '@lingui/macro';
import { Checkbox } from '@toqio/toqio-web-components';
import { DownloadGlyph, UploadGlyph } from './glyphs';
import { tok, UN_BLUE } from './styles';

/**
 * Right column — the header controls from the live page (PRD §2 phase 1):
 * Execute Payments checkbox, "Upload file for delegate creation" action (icon +
 * title + hint, triggers the hidden file input) and "Download example file".
 * Exact metrics from the live sandbox: label 16/400, titles 16/600, hints
 * 16/400 #696969, download link 16/700 #1C4969.
 */
const DOWNLOAD_NAVY = '#1C4969';

const Col = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CheckboxLabel = styled.span`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const ActionRow = styled.button<{ $gap: number }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ $gap }) => $gap}px;
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  text-align: left;
  cursor: pointer;
  font: inherit;
  color: inherit;
`;

const IconCol = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  height: 24px;
`;

const Title = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: ${({ theme }) => tok(theme, 'general_font_primary', '#1E1E1E')};
`;

const Hint = styled.div`
  font-size: 16px;
  line-height: 24px;
  font-weight: 400;
  color: ${({ theme }) => tok(theme, 'general_font_tertiary', '#696969')};
`;

const LinkLabel = styled.span`
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  color: ${DOWNLOAD_NAVY};
`;

export type ActionsProps = {
  executePayments: boolean;
  onExecutePaymentsChange: (value: boolean) => void;
  onUploadClick: () => void;
  onDownloadExample: () => void;
};

export const Actions: React.FC<ActionsProps> = ({
  executePayments,
  onExecutePaymentsChange,
  onUploadClick,
  onDownloadExample,
}) => (
  <Col>
    <Checkbox
      label={<CheckboxLabel>{t`Execute Payments`}</CheckboxLabel>}
      description={t`Payments will be paid from the account of this conference in the appropriate currency labelled as Main.`}
      checked={executePayments}
      onChange={onExecutePaymentsChange}
    />

    <ActionRow type="button" $gap={16} onClick={onUploadClick}>
      <IconCol>
        <UploadGlyph size={24} color={UN_BLUE} />
      </IconCol>
      <div>
        <Title>
          <Trans>Upload file for delegate creation</Trans>
        </Title>
        <Hint>
          <Trans>Only CSV files are accepted</Trans>
        </Hint>
      </div>
    </ActionRow>

    <ActionRow type="button" $gap={8} onClick={onDownloadExample}>
      <IconCol>
        <DownloadGlyph size={20} color={DOWNLOAD_NAVY} />
      </IconCol>
      <LinkLabel>
        <Trans>Download example file</Trans>
      </LinkLabel>
    </ActionRow>
  </Col>
);
